import React, { useState, useEffect, useRef, DragEvent, KeyboardEvent } from 'react';
import type { AttachmentGroupDto } from '../../services/attachments/attachment.types';
import { createAttachmentGroup, updateAttachmentGroup, uploadAttachment, deleteAttachment, downloadAttachment } from '../../services/attachments/attachment.service';
import generateUid from '../../utils/generateUid';
import ActionList from '../ui/ActionsList';
import { StatusMessage } from '../ui';
import Modal from '../ui/Modal';
import { ModalContent } from '../ui/ModalContent';

interface AttachmentGroupsFormData {
  profileName: string;
  files?: File[];
}

interface AttachmentGroupsFormProps {
  onSubmit?: (group: AttachmentGroupDto) => void;
  onCancel?: () => void;
  initialData?: Partial<AttachmentGroupsFormData & { id?: number; existingAttachments?: any[]; siteRuleCount?: number }>; // id for edit
  isLoading?: boolean;
  groupId?: number;
  onSubmitting?: (submitting: boolean) => void;
}

export const AttachmentGroupsForm: React.FC<AttachmentGroupsFormProps> = ({ onSubmit, onCancel, initialData = {}, isLoading = false, groupId, onSubmitting }) => {
  const [profileName, setProfileName] = useState<string>(initialData?.profileName ?? '');
  type LocalFile = { file: File; originalName: string };
  const [files, setFiles] = useState<LocalFile[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<number | null>(null);
  const [existingAttachments, setExistingAttachments] = useState<any[]>(initialData?.existingAttachments || []);

  //modal variables
  const [openModalConfirm, setOpenModalConfirm] = useState<boolean>(false);
  const [modalHeadingText, setModalHeadingText] = useState<string | undefined>(undefined);
  const [modalMessageText, setModalMessageText] = useState<string | undefined>(undefined);
  const resolverRef = useRef<((value: boolean) => void) | null>(null);
  
  useEffect(() => {
    setProfileName(initialData?.profileName ?? '');
    setExistingAttachments(initialData?.existingAttachments || []);
    // no initial files - attachments are uploaded separately
  }, [initialData?.profileName, initialData?.existingAttachments]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files;
    if (!f) return;
    const incoming = Array.from(f);
    const sanitizedIncomingPromises = incoming.map(sanitizeFile);
    Promise.all(sanitizedIncomingPromises).then(sanitizedIncoming => {
      const { accepted, rejected } = validateFiles(sanitizedIncoming);
      if (rejected.length > 0) {
        setError(`Only PDF and TXT files are allowed: ${rejected.map(r => r.originalName).join(', ')}`);
      }
      // Only add accepted files to the client-side list; rejected files are
      // reported via `setError` but should not appear in the table.
      if (accepted.length > 0) {
        setFiles(prev => ([...prev, ...accepted]));
      }
      // reset input
      e.currentTarget.value = '';
    });
  };

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const profileNameRef = useRef<HTMLInputElement | null>(null);
  const validationRef = useRef<HTMLDivElement | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  type FileValidationResult = { accepted: LocalFile[]; rejected: LocalFile[] };
  const validateFiles = (incoming: LocalFile[]): FileValidationResult => {
    const accepted: LocalFile[] = [];
    const rejected: LocalFile[] = [];
    const allowExt = ['.pdf', '.txt'];
    for (const lf of incoming) {
      const name = lf.originalName || lf.file.name || '';
      const lower = name.toLowerCase();
      const matches = allowExt.some(ext => lower.endsWith(ext));
      if (matches) accepted.push(lf);
      else rejected.push(lf);
    }
    return { accepted, rejected };
  };

  const isAllowedFile = (lf: LocalFile) => {
    const allowExt = ['.pdf', '.txt'];
    const name = lf.originalName || lf.file.name || '';
    const lower = name.toLowerCase();
    return allowExt.some(ext => lower.endsWith(ext));
  };

  const openFilePicker = () => fileInputRef.current?.click();

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const dt = e.dataTransfer;
    if (!dt) return;
    const { files: droppedFiles } = dt;
    if (!droppedFiles || droppedFiles.length === 0) return;
    const incoming = Array.from(droppedFiles);
    const sanitizedIncomingPromises = incoming.map(sanitizeFile);
    Promise.all(sanitizedIncomingPromises).then(sanitizedIncoming => {
      const { accepted, rejected } = validateFiles(sanitizedIncoming);
      if (rejected.length > 0) {
        setError(`Only PDF and TXT files are allowed: ${rejected.map(r => (r as unknown as LocalFile).originalName).join(', ')}`);
      }
      if (accepted.length > 0) {
        setFiles(prev => ([...prev, ...accepted as unknown as LocalFile[]]));
      }
    });
  };

  // Frontend-only sanitization: rename to UID, do lightweight checks
  async function sanitizeFile(f: File): Promise<LocalFile> {
    const nameLower = f.name.toLowerCase();

    // If looks like PDF and has PDF header, keep content but rename
    if (nameLower.endsWith('.pdf')) {
      // quick header check using TextDecoder to avoid char helpers
      const headerBuf = await f.slice(0, 8).arrayBuffer();
      const headerStr = new TextDecoder('utf-8', { fatal: false }).decode(headerBuf);
      if (!headerStr.startsWith('%PDF-')) {
        // return as LocalFile but with a flagged original name so validation will reject it
        return { file: f, originalName: `invalid-${generateUid()}-${f.name}` };
      }
      const uidName = `${generateUid()}.pdf`;
      return { file: new File([f], uidName, { type: 'application/pdf' }), originalName: f.name };
    }

    // For text files: normalize to UTF-8, strip NULs and control characters (except newline/tab)
    if (nameLower.endsWith('.txt')) {
      const buf = await f.arrayBuffer();
      // Decode as UTF-8 leniently
      const decoder = new TextDecoder('utf-8', { fatal: false });
      let text = decoder.decode(buf);

      // Helper: remove control characters except TAB(9), LF(10), CR(13)
      const removeControlChars = (input: string) => {
        let out = '';
        // Iterate over actual Unicode characters (handles surrogate pairs correctly)
        for (const ch of input) {
          const cp = ch.codePointAt(0);
          if (cp === undefined) continue;
          // Allow horizontal tab (9), line feed (10), carriage return (13)
          if (cp === 9 || cp === 10 || cp === 13) {
            out += ch;
            continue;
          }
          // Allow printable characters (>= 32) except DEL (127)
          if (cp >= 32 && cp !== 127) {
            out += ch;
          }
          // Otherwise skip the character
        }
        return out;
      };

      text = removeControlChars(text);
      const uidNameTxt = `${generateUid()}.txt`;
      return { file: new File([text], uidNameTxt, { type: 'text/plain;charset=utf-8' }), originalName: f.name };
    }

    // For other types, just rename to UID and keep content
    const uidName = `${generateUid()}-${f.name}`;
    return { file: new File([f], uidName, { type: f.type }), originalName: f.name };
  }

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setIsDragOver(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDropKey = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openFilePicker();
    }
  };

  const handleRemoveFile = (idx: number) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleDownloadAttachment = async (att: any) => {
    try {
      // If the attachment has a URL field, use it directly
      if (att.url) {
        console.info('Using attachment URL:', att.url);
        const link = document.createElement('a');
        link.href = att.url;
        // prefer originalFileName for the suggested download name
        link.download = att.originalFileName ?? att.fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        return;
      }
      
      // Otherwise, try the download service
      await downloadAttachment(att.id, att.originalFileName ?? att.fileName);
    } catch (err) {
      console.info('Download failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to download attachment');
    }
  };

  const handleDeleteAttachment = async (att: any) => {
    const displayName = att.originalFileName ?? att.fileName;
    setModalMessageText(`Are you sure you want to delete "${displayName}"?`);
    setModalHeadingText("Delete Attachment");
    setOpenModalConfirm(true);

    const confirmed = await waitForModalConfirm();

    if (!confirmed) return;
    
    setDeletingAttachmentId(att.id);
    try {
      const res = await deleteAttachment(att.id);
      if (res.success) {
        // Remove from local state
        setExistingAttachments(prev => prev.filter(a => a.id !== att.id));
      } else {
        setError(`Failed to delete ${displayName}: ${res.error?.message || 'Unknown error'}`);
      }
    } catch (err) {
      setError(`Failed to delete ${displayName}: ${(err as Error).message}`);
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const waitForModalConfirm = (): Promise<boolean> => {
    return new Promise((resolve) => {
      (resolverRef as React.MutableRefObject<((value: boolean) => void) | null>).current = resolve;
      setOpenModalConfirm(true);
    });
  };

  // helper: create or update group
  const submitCreateOrUpdate = async (gid?: number) => {
    if (gid) {
      const payload = { id: gid, profileName } as any;
      const res = await updateAttachmentGroup(gid, payload);
      return res.data;
    }
    const res = await createAttachmentGroup({ profileName });
    return res.data;
  };

  const uploadSelectedFiles = async (gid: number) => {
    if (!files || files.length === 0) return;
    // Only upload files that are allowed; skip invalid ones and report them.
    const allowedToUpload = files.filter(isAllowedFile);
    const invalidFiles = files.filter(f => !isAllowedFile(f));
    if (invalidFiles.length > 0) {
      setError(`Some files were not uploaded because they are not allowed: ${invalidFiles.map(f => f.originalName).join(', ')}`);
    }
    if (allowedToUpload.length === 0) return;
    const uploadErrors: string[] = [];
    for (const lf of allowedToUpload) {
      try {
        console.info('Uploading file to group', gid, ':', lf.originalName);
        const result = await uploadAttachment({ groupId: String(gid), file: lf.file, fileName: lf.file.name, originalFileName: lf.originalName });
        if (result.success) {
          console.info('Upload successful:', lf.originalName);
          // If the server returned attachment metadata, merge it into existingAttachments.
          // Prefer server-provided originalFileName, otherwise use the client-side originalName.
          const serverAtt = result.data || {};
          const attToAdd = { ...serverAtt, originalFileName: serverAtt.originalFileName ?? lf.originalName };
          setExistingAttachments(prev => ([...prev, attToAdd]));
        } else {
          uploadErrors.push(`${lf.originalName}: ${result.error?.message || 'Upload failed'}`);
        }
      } catch (err) {
        console.info('Failed to upload attachment', lf, err);
        uploadErrors.push(`${lf.originalName}: ${(err as Error).message || 'Unknown error'}`);
      }
    }
    if (uploadErrors.length > 0) {
      throw new Error(`Some files failed to upload:\n${uploadErrors.join('\n')}`);
    }
  };

  const finishSubmit = () => {
    setProfileName('');
    setFiles([]);
    setSubmitting(false);
    onSubmitting?.(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // validate profile name like DistributionGroups: show top-level validation
    if (!profileName || profileName.trim() === '') {
      setError('Profile name is required');
      // focus the profile input
      profileNameRef.current?.focus();
      return;
    }
    setSubmitting(true);
    onSubmitting?.(true);
    setError(null);
    try {
      const createdGroup = await submitCreateOrUpdate(groupId);
      if (createdGroup?.id) {
        await uploadSelectedFiles(createdGroup.id);
      }
      if (createdGroup) onSubmit?.(createdGroup);
      finishSubmit();
    } catch (err: any) {
      setError(err?.message || String(err));
      setSubmitting(false);
      onSubmitting?.(false);
    }
  };

  // When a form-level error appears, scroll the validation summary into view so
  // users see the message (useful when the modal content is scrolled).
  useEffect(() => {
    if (error) {
      // allow layout to settle
      requestAnimationFrame(() => {
        validationRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }
  }, [error]);

  return (
    <div className="mb-2">

      <Modal
        isOpen={openModalConfirm}
        title={modalHeadingText ?? ''}
        headerClass="modal__header--danger"
        content={<ModalContent heading={modalHeadingText} message={modalMessageText} />}
        onConfirm={() => {
          resolverRef.current?.(true);
          setOpenModalConfirm(false);
        }}
        onCancel={() => {
          resolverRef.current?.(false);
          setOpenModalConfirm(false);
        }}
      />
      <form onSubmit={handleSubmit} className="form">
        <h3 className="heading__title heading__title--attachment-groups">{groupId ? 'Edit Attachment Group' : 'Add Attachment Group'}</h3>
        {error && (
          <div className="mt-2" ref={validationRef}>
            <StatusMessage kind="error" onClose={() => setError(null)}>{error}</StatusMessage>
          </div>
        )}
        
        {/* Show existing attachments and associations when editing */}
        <div className="form__group mt-2">
          <label htmlFor="profileName" className="form__label form__label--required">Profile Name</label>
          <input
            ref={profileNameRef}
            id="profileName"
            name="profileName"
            className="form__control"
            value={profileName}
            onChange={e => { setProfileName(e.target.value); if (e.target.value.trim()) setError(null); }}
            disabled={isLoading || submitting}
          />
        </div>

        {groupId && initialData && (
          <div className="form__group">
            <div className="">
              <h4>Existing Attachments</h4>
              {existingAttachments && existingAttachments.length > 0 ? (
                <>
                  <p><strong>{existingAttachments.length} attachment(s):</strong></p>
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>File Name</th>
                          <th>Size</th>
                          <th>Type</th>
                          <th>Uploaded</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {existingAttachments.map((att: any) => (
                          <tr key={att.id}>
                            <td>{att.originalFileName ?? att.fileName}</td>
                            <td>{att.fileSize || 'N/A'}</td>
                            <td>{att.fileType || 'N/A'}</td>
                            <td>{att.uploadedDateTime || 'N/A'}</td>
                            <td>
                              <ActionList
                                customActions={[
                                  {
                                    label: 'Download',
                                    onClick: () => { void handleDownloadAttachment(att); },
                                    className: 'actions__item actions__item--download',
                                    disabled: deletingAttachmentId !== null,
                                  },
                                  {
                                    label: 'Delete',
                                    onClick: () => { void handleDeleteAttachment(att); },
                                    className: 'actions__item actions__item--delete',
                                    disabled: deletingAttachmentId !== null,
                                  },
                                ]}
                                disabled={deletingAttachmentId !== null}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <p>No attachments</p>
              )}
              
            </div>
          </div>
        )}

        <div className="form__group--attachments">
          <label className="form__label">Attachments</label>
          <div
            className={`dropzone ${isDragOver ? 'dropzone--over' : ''}`}
            onClick={openFilePicker}
            onKeyDown={handleDropKey}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragEnter={handleDragOver}
            onDragLeave={handleDragLeave}
            role="button"
            tabIndex={0}
            aria-label="Drop files here or click to select files"
          >
            <input ref={fileInputRef} type="file" accept=".pdf,.txt" multiple onChange={handleFileChange} disabled={isLoading || submitting} style={{ display: 'none' }} />
            <div className="dropzone__content">
              <p className="dropzone__hint">Drag & drop files here, or click to browse</p>
            </div>
          </div>
          {files.length > 0 && (
            <div className="table-responsive attachments__new mt-2">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>File Name</th>
                    <th>Size</th>
                    <th>Type</th>
                    <th>Uploaded</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {files.map((lf, i) => (
                    <tr key={`${lf.file.name}-${lf.file.lastModified}`}>
                      <td>{lf.originalName}</td>
                      <td>{(lf.file.size / 1024).toFixed(1)} KB</td>
                      <td>{isAllowedFile(lf) ? (lf.file.type || 'N/A') : <span className="text-danger">Invalid type</span>}</td>
                      <td>{'Pending'}</td>
                      <td>
                        <ActionList
                          customActions={[
                            {
                              label: 'Remove',
                              onClick: () => { handleRemoveFile(i); },
                              className: 'actions__item actions__item--delete',
                              disabled: false,
                            },
                          ]}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal footer handles Cancel/Confirm actions; inline buttons removed to avoid duplication. */}
      </form>
    </div>
  );
};

export default AttachmentGroupsForm;
